import React, { useState, useMemo } from 'react';
import { User } from 'lucide-react';
import { CONTACT_CATEGORIES, BANK_SUGGESTIONS } from './helpers';

// ---------- field-set per category ----------
const FIELDS_BY_CAT = {
  bank_staff: ['bank', 'branch', 'designation'],
  client: ['property_location'],
  dsa: ['agency'],
  referral: ['relation'],
};
const FIELD_LABEL = {
  bank: 'Bank Name', branch: 'Branch', designation: 'Designation',
  agency: 'Agency / DSA Firm', relation: 'Relation', property_location: 'Property Location',
};
const FIELD_PLACEHOLDER = {
  designation: 'e.g. Branch Manager, Loan Officer',
  agency: 'e.g. SBI DSA, Independent',
  relation: 'e.g. Friend, Family, Colleague',
};

// ---------- sub-components ----------
const CategoryChips = ({ value, onChange }) => (
  <div className="contact-cat-chips" data-testid="contact-cat-chips">
    {CONTACT_CATEGORIES.map((c) => (
      <button key={c.id} type="button"
        className={`contact-cat-chip cat-${c.id}${value === c.id ? ' selected' : ''}`}
        onClick={() => onChange(c.id)}
        data-testid={`contact-cat-${c.id}`}>
        {c.label}
      </button>
    ))}
  </div>
);

const BankPicker = ({ pick, onPickChange, other, onOtherChange }) => (
  <>
    <div className="bank-chips" data-testid="contact-bank-chips">
      {BANK_SUGGESTIONS.map((b) => (
        <button key={b} type="button"
          className={`bank-chip${pick === b ? ' selected' : ''}`}
          onClick={() => onPickChange(b)}
          data-testid={`contact-bank-chip-${b.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
          {b}
        </button>
      ))}
      <button type="button"
        className={`bank-chip bank-other${pick === '__other__' ? ' selected' : ''}`}
        onClick={() => onPickChange('__other__')}
        data-testid="contact-bank-chip-other">
        + Other (type below)
      </button>
    </div>
    {pick === '__other__' && (
      <input className="field-input" style={{ marginTop: 8 }}
        value={other} onChange={(e) => onOtherChange(e.target.value)}
        placeholder="Type bank name…" data-testid="contact-bank-other" />
    )}
  </>
);

const TextField = ({ label, value, onChange, placeholder, testid, autoFocus }) => (
  <div className="field">
    <label className="field-label">{label}</label>
    <input className="field-input" value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      data-testid={testid} />
  </div>
);

// ---------- main ----------
export default function ContactModal({ contact, defaultCategory, prefill, onClose, onSave }) {
  const isEdit = !!contact;
  const initial = contact || prefill || {};

  const [name, setName] = useState(initial.name || '');
  const [mobile, setMobile] = useState(initial.mobile || '');
  const [email, setEmail] = useState(initial.email || '');
  const [category, setCategory] = useState(initial.category || defaultCategory || 'client');
  const [branch, setBranch] = useState(initial.branch || '');
  const [designation, setDesignation] = useState(initial.designation || '');
  const [agency, setAgency] = useState(initial.agency || '');
  const [relation, setRelation] = useState(initial.relation || '');
  const [propertyLocation, setPropertyLocation] = useState(initial.property_location || '');
  const [notes, setNotes] = useState(initial.notes || '');
  const [saving, setSaving] = useState(false);

  // Bank chip state
  const initialBank = initial.bank || '';
  const initialBankIsPreset = BANK_SUGGESTIONS.includes(initialBank);
  const [bankPick, setBankPick] = useState(initialBank ? (initialBankIsPreset ? initialBank : '__other__') : '');
  const [bankOther, setBankOther] = useState(initialBank && !initialBankIsPreset ? initialBank : '');
  const bankFinal = bankPick === '__other__' ? bankOther.trim() : bankPick;

  const fields = useMemo(() => FIELDS_BY_CAT[category] || [], [category]);
  const canSave = !!name.trim() && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        category,
        bank: fields.includes('bank') ? bankFinal : '',
        branch: fields.includes('branch') ? branch.trim() : '',
        designation: fields.includes('designation') ? designation.trim() : '',
        agency: fields.includes('agency') ? agency.trim() : '',
        relation: fields.includes('relation') ? relation.trim() : '',
        property_location: fields.includes('property_location') ? propertyLocation.trim() : '',
        notes: notes.trim(),
      };
      await onSave(isEdit ? { mode: 'edit', id: contact.id, data: payload } : { mode: 'create', data: payload });
    } finally { setSaving(false); }
  };

  const onOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-overlay" onClick={onOverlayClick}>
      <div className="modal" data-testid="contact-modal">
        <div className="modal-header">
          <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <User style={{ width: 16, height: 16 }} /> {isEdit ? 'Edit Contact' : 'New Contact'}
          </h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="field">
          <label className="field-label">Category *</label>
          <CategoryChips value={category} onChange={setCategory} />
        </div>

        <TextField label="Name *" value={name} onChange={setName} testid="contact-name" autoFocus />

        <div className="field-row">
          <TextField label="Mobile" value={mobile}
            onChange={(v) => setMobile(v.replace(/[^0-9]/g, '').slice(0, 10))}
            testid="contact-mobile" />
          <TextField label="Email" value={email} onChange={setEmail} testid="contact-email" />
        </div>

        {fields.includes('bank') && (
          <div className="field">
            <label className="field-label">{FIELD_LABEL.bank}</label>
            <BankPicker pick={bankPick} onPickChange={setBankPick}
              other={bankOther} onOtherChange={setBankOther} />
          </div>
        )}

        {fields.includes('branch') && (
          <TextField label={FIELD_LABEL.branch} value={branch} onChange={setBranch} testid="contact-branch" />
        )}
        {fields.includes('designation') && (
          <TextField label={FIELD_LABEL.designation} value={designation} onChange={setDesignation}
            placeholder={FIELD_PLACEHOLDER.designation} testid="contact-designation" />
        )}
        {fields.includes('agency') && (
          <TextField label={FIELD_LABEL.agency} value={agency} onChange={setAgency}
            placeholder={FIELD_PLACEHOLDER.agency} testid="contact-agency" />
        )}
        {fields.includes('relation') && (
          <TextField label={FIELD_LABEL.relation} value={relation} onChange={setRelation}
            placeholder={FIELD_PLACEHOLDER.relation} testid="contact-relation" />
        )}
        {fields.includes('property_location') && (
          <TextField label={FIELD_LABEL.property_location} value={propertyLocation}
            onChange={setPropertyLocation} testid="contact-property-location" />
        )}

        <div className="field">
          <label className="field-label">Notes</label>
          <textarea className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} placeholder="Optional notes…" data-testid="contact-notes" />
        </div>

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={submit}
            disabled={!canSave} data-testid="save-contact-btn">
            {saving ? 'Saving…' : isEdit ? 'Update Contact' : 'Save Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
